import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, setToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();

  const [step, setStep] = useState("credentials"); // credentials | 2fa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [code, setCode] = useState("");
  const [isBackup, setIsBackup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const target = location.state?.from?.pathname || "/admin";

  const submitCreds = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.status === "2fa_required") {
        setChallengeToken(data.challenge_token);
        setStep("2fa");
        toast.message("Two-factor authentication required");
      } else {
        setToken(data.token);
        await loginWithToken(data.token);
        navigate(target, { replace: true });
      }
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", {
        challenge_token: challengeToken,
        code,
        is_backup_code: isBackup,
      });
      setToken(data.token);
      await loginWithToken(data.token);
      if (data.must_reenroll_2fa) {
        toast.warning("2FA disabled via recovery. Please re-enable it now.");
        navigate("/admin/profile", { replace: true });
      } else {
        navigate(target, { replace: true });
      }
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      <header className="px-6 sm:px-12 pt-10 max-w-6xl w-full mx-auto">
        <Link to="/" data-testid="login-brand-link">
          <Brand size="sm" subtitle={true} />
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80 mb-3">Administration</p>
            <h1 className="font-display text-4xl sm:text-5xl">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-3">
              {step === "credentials"
                ? "Use your King's Café admin credentials."
                : isBackup
                ? "Enter one of your 8-char backup codes (XXXX-XXXX)."
                : "Enter the 6-digit code from your authenticator app."}
            </p>
          </div>

          <div className="border border-border rounded-2xl bg-card p-7 gold-glow">
            {step === "credentials" ? (
              <form onSubmit={submitCreds} className="space-y-5" data-testid="login-credentials-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@kingscafe.example"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                  />
                </div>
                {error && (
                  <p
                    role="alert"
                    aria-live="polite"
                    className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2"
                    data-testid="login-error"
                  >
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="login-submit-button"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submit2fa} className="space-y-5" data-testid="login-2fa-form">
                <div className="flex items-center gap-2 text-primary text-sm">
                  {isBackup ? <KeyRound className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                  <span>{isBackup ? "Backup code" : "Authenticator code"}</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp">{isBackup ? "Backup code" : "6-digit code"}</Label>
                  <Input
                    id="otp"
                    inputMode={isBackup ? "text" : "numeric"}
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={isBackup ? "XXXX-XXXX" : "123456"}
                    maxLength={isBackup ? 9 : 6}
                    className="tracking-widest text-center text-lg"
                    data-testid="login-2fa-code-input"
                    autoFocus
                  />
                </div>
                {error && (
                  <p
                    role="alert"
                    aria-live="polite"
                    className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2"
                    data-testid="login-2fa-error"
                  >
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="login-2fa-submit-button"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
                </Button>
                <div className="text-xs text-center pt-2 space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsBackup(!isBackup);
                      setCode("");
                      setError("");
                    }}
                    className="text-primary hover:underline"
                    data-testid="login-2fa-toggle-backup"
                  >
                    {isBackup ? "Use authenticator code instead" : "Use backup code instead"}
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("credentials");
                      setCode("");
                      setError("");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="login-2fa-back"
                  >
                    Back
                  </button>
                </div>
              </form>
            )}
          </div>

          {step === "credentials" && (
            <div className="mt-6 flex flex-col items-center gap-2 text-sm">
              <Link
                to="/admin/forgot-password"
                className="text-muted-foreground hover:text-primary"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </Link>
              <Link
                to="/admin/recover"
                className="text-muted-foreground hover:text-primary"
                data-testid="login-recover-link"
              >
                Locked out? Recover access
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
